import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Trophy, 
  Star, 
  Clock, 
  Ban,
  AlertTriangle,
  RefreshCw,
  ArrowLeft,
  TrendingUp,
  Users,
  CheckCircle,
  XCircle,
  Eye,
  RotateCcw
} from 'lucide-react';

export default function ProviderScores() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [scores, setScores] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [providerDetail, setProviderDetail] = useState(null);
  const [recalculating, setRecalculating] = useState(false);

  useEffect(() => {
    if (!user || user.user_type !== 'admin') {
      navigate('/control-portal-clube/login');
      return;
    }
    fetchScores();
  }, [user, navigate, filterStatus]);

  const fetchScores = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterStatus !== 'all') {
        params.append('quality_status', filterStatus);
      }
      params.append('limit', '100');
      
      const res = await axios.get(`${API}/admin/provider-scores?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setScores(res.data.scores || []);
      setStats(res.data.stats || {});
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Erro ao buscar scores:', error);
      toast.error('Erro ao carregar scores');
    } finally {
      setLoading(false);
    }
  };

  const fetchProviderDetail = async (providerId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/provider-scores/${providerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProviderDetail(res.data);
      setDetailModalOpen(true);
    } catch (error) {
      console.error('Erro ao buscar detalhes:', error);
      toast.error('Erro ao carregar detalhes do prestador');
    }
  };

  const recalculateScore = async (providerId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/provider-scores/${providerId}/recalculate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Score recalculado com sucesso');
      fetchScores();
      if (providerDetail && providerDetail.provider?.id === providerId) {
        fetchProviderDetail(providerId);
      }
    } catch (error) {
      console.error('Erro ao recalcular:', error);
      toast.error('Erro ao recalcular score');
    }
  };

  const recalculateAll = async () => {
    setRecalculating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/provider-scores/recalculate-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchScores();
    } catch (error) {
      console.error('Erro ao recalcular todos:', error);
      toast.error('Erro ao recalcular scores');
    } finally {
      setRecalculating(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'good':
        return <Badge className="bg-green-100 text-green-700">Bom</Badge>;
      case 'low_quality':
        return <Badge className="bg-yellow-100 text-yellow-700">Baixa Qualidade</Badge>;
      case 'blocked':
        return <Badge className="bg-red-100 text-red-700">Bloqueado</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatPercent = (value) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/control-portal-clube/dashboard')}
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Trophy className="w-6 h-6 text-yellow-500" />
                  Score de Prestadores
                </h1>
                <p className="text-sm text-muted-foreground">
                  Ranking de qualidade e confiabilidade
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="good">Bom</SelectItem>
                  <SelectItem value="low_quality">Baixa Qualidade</SelectItem>
                  <SelectItem value="blocked">Bloqueado</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                variant="outline"
                onClick={recalculateAll}
                disabled={recalculating}
                className="gap-2"
              >
                <RotateCcw className={`w-4 h-4 ${recalculating ? 'animate-spin' : ''}`} />
                Recalcular Todos
              </Button>
              <Button 
                variant="outline" 
                onClick={fetchScores}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total de Prestadores
              </CardTitle>
              <Users className="w-4 h-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{total}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Score Bom
              </CardTitle>
              <CheckCircle className="w-4 h-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {stats.good?.count || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                Média: {stats.good?.avg_score?.toFixed(1) || '-'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Baixa Qualidade
              </CardTitle>
              <AlertTriangle className="w-4 h-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">
                {stats.low_quality?.count || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                Média: {stats.low_quality?.avg_score?.toFixed(1) || '-'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Bloqueados
              </CardTitle>
              <Ban className="w-4 h-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {stats.blocked?.count || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                Média: {stats.blocked?.avg_score?.toFixed(1) || '-'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Scores Table */}
        <Card>
          <CardHeader>
            <CardTitle>Ranking de Prestadores</CardTitle>
            <CardDescription>
              Ordenado por score (maior para menor)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {scores.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">#</TableHead>
                    <TableHead>Prestador</TableHead>
                    <TableHead className="text-center">Score</TableHead>
                    <TableHead className="text-center">⭐ Avaliação</TableHead>
                    <TableHead className="text-center">❌ Cancelamento</TableHead>
                    <TableHead className="text-center">⏱️ Resposta</TableHead>
                    <TableHead className="text-center">⚠️ Disputas</TableHead>
                    <TableHead className="text-center">Serviços</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {scores.map((score, idx) => (
                    <TableRow key={score.provider_id}>
                      <TableCell className="font-bold">
                        {idx < 3 ? (
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            idx === 0 ? 'bg-yellow-500 text-white' :
                            idx === 1 ? 'bg-slate-400 text-white' :
                            'bg-amber-700 text-white'
                          }`}>
                            {idx + 1}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">{idx + 1}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">
                            {score.provider?.business_name || score.provider?.name || 'N/A'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {score.provider?.email}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={`text-xl font-bold ${getScoreColor(score.score)}`}>
                          {score.score?.toFixed(0)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          <span>{score.rating_average?.toFixed(1)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {formatPercent(score.cancel_rate || 0)}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span>{score.response_time?.toFixed(1)}h</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {formatPercent(score.dispute_rate || 0)}
                      </TableCell>
                      <TableCell className="text-center font-medium">
                        {score.completed_services || 0}
                      </TableCell>
                      <TableCell className="text-center">
                        {getStatusBadge(score.quality_status)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => fetchProviderDetail(score.provider_id)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => recalculateScore(score.provider_id)}
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                {loading ? (
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto" />
                ) : (
                  'Nenhum prestador encontrado'
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Como o Score é Calculado</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-semibold text-yellow-600">⭐ Avaliação (40%)</p>
                <p className="text-muted-foreground">Média das avaliações dos clientes</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-semibold text-red-600">❌ Cancelamento (25%)</p>
                <p className="text-muted-foreground">Menos cancelamentos = melhor score</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-semibold text-blue-600">⏱️ Resposta (20%)</p>
                <p className="text-muted-foreground">Tempo médio para aceitar serviço</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-semibold text-orange-600">⚠️ Disputas (15%)</p>
                <p className="text-muted-foreground">Menos disputas = melhor score</p>
              </div>
            </div>
            <div className="mt-4 p-3 bg-yellow-50 rounded-lg text-sm">
              <p><strong>Score &lt; 40:</strong> Marcado como "Baixa Qualidade" - Alerta no admin</p>
              <p><strong>Score &lt; 25:</strong> Bloqueado automaticamente - Não recebe novos agendamentos</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalhes do Score</DialogTitle>
            <DialogDescription>
              {providerDetail?.provider?.business_name || providerDetail?.provider?.name}
            </DialogDescription>
          </DialogHeader>
          
          {providerDetail && (
            <div className="space-y-4">
              {/* Score Breakdown */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <p className={`text-4xl font-bold ${getScoreColor(providerDetail.score?.score || 0)}`}>
                    {providerDetail.score?.score?.toFixed(0) || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Score Total</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium mb-2">Breakdown</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Avaliação:</span>
                      <span className="font-medium">{providerDetail.score?.score_breakdown?.rating?.toFixed(1) || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Cancelamento:</span>
                      <span className="font-medium">{providerDetail.score?.score_breakdown?.cancel?.toFixed(1) || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Resposta:</span>
                      <span className="font-medium">{providerDetail.score?.score_breakdown?.response?.toFixed(1) || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Disputas:</span>
                      <span className="font-medium">{providerDetail.score?.score_breakdown?.dispute?.toFixed(1) || 0}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="p-2 border rounded">
                  <p className="text-lg font-bold">{providerDetail.score?.rating_average?.toFixed(1)}</p>
                  <p className="text-xs text-muted-foreground">Avaliação</p>
                </div>
                <div className="p-2 border rounded">
                  <p className="text-lg font-bold">{formatPercent(providerDetail.score?.cancel_rate || 0)}</p>
                  <p className="text-xs text-muted-foreground">Cancelamento</p>
                </div>
                <div className="p-2 border rounded">
                  <p className="text-lg font-bold">{providerDetail.score?.response_time?.toFixed(1)}h</p>
                  <p className="text-xs text-muted-foreground">Resposta</p>
                </div>
                <div className="p-2 border rounded">
                  <p className="text-lg font-bold">{formatPercent(providerDetail.score?.dispute_rate || 0)}</p>
                  <p className="text-xs text-muted-foreground">Disputas</p>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">Status de Qualidade</p>
                  <p className="text-sm text-muted-foreground">
                    {providerDetail.score?.completed_services || 0} serviços concluídos • {providerDetail.score?.reviews_count || 0} avaliações
                  </p>
                </div>
                {getStatusBadge(providerDetail.score?.quality_status)}
              </div>

              {/* Alerts */}
              {providerDetail.alerts?.length > 0 && (
                <div>
                  <p className="font-medium mb-2">Alertas Recentes</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {providerDetail.alerts.map((alert, idx) => (
                      <div key={idx} className="p-2 bg-red-50 border border-red-200 rounded text-sm">
                        <p>{alert.message}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(alert.created_at).toLocaleString('pt-BR')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
